import { Injectable, inject } from '@angular/core';
import { ParamMap, Router } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { ElectronService } from '../../../core/services/electron.service';

interface RouteDirectlyArgs {
  error: string | null;
  pendingActivation: boolean;
  requiresCompanySetup: boolean;
  setupToken: string | null;
  planName: string | null;
  coupon: string | null;
  checkoutUrl: string | null;
  expiresAt: number | null;
  requires2fa: boolean;
  mfaToken: string | null;
  /** Handed back by the API after the Google round trip; see `AuthService.rememberReturnUrl`. */
  returnUrl: string | null;
}

@Injectable({ providedIn: 'root' })
export class GoogleCallbackService {
  private router = inject(Router);
  private authService = inject(AuthService);
  private electronService = inject(ElectronService);

  handleCallback(params: ParamMap): void {
    const rawExpiresAt = params.get('expires_at');
    const expiresAt = rawExpiresAt ? Number(rawExpiresAt) : null;

    const args: RouteDirectlyArgs = {
      pendingActivation: params.get('pending_activation') === 'true',
      expiresAt: expiresAt,
      requires2fa: params.get('requires_2fa') === 'true',
      mfaToken: params.get('mfa_token'),
      error: params.get('error'),
      checkoutUrl: params.get('checkout_url'),
      coupon: params.get('coupon'),
      requiresCompanySetup: params.get('requires_company_setup') === 'true',
      setupToken: params.get('setup_token'),
      planName: params.get('planName'),
      returnUrl: params.get('return_url'),
    };

    const exchangeToken = params.get('exchange_token');

    // --- ELECTRON DESKTOP FLOW ---
    if (this.electronService.isElectron) {
      if (exchangeToken) {
        this.authService.exchangeDesktopToken(exchangeToken).subscribe({
          next: res => this.routeDirectly({ ...args, expiresAt: res.expires_at }),
          error: () => this.router.navigate(['/login']),
        });
      } else {
        this.routeDirectly(args);
      }
      return;
    }

    // --- WEB POPUP FLOW ---
    const payload = {
      type: args.error ? 'google-auth-error' : 'google-auth-success',
      ...args,
    };

    if (!!window.opener && window.opener !== window) {
      try {
        window.opener.postMessage(payload, '*');
      } catch {
        console.debug('Cross-origin opener restricted, falling back to local storage.');
      }
    }

    localStorage.setItem('google_auth_fallback', JSON.stringify({ ...payload, timestamp: Date.now() }));
    window.close();

    setTimeout(() => {
      if (!window.closed) this.routeDirectly(args);
    }, 150);
  }

  private routeDirectly(args: RouteDirectlyArgs): void {
    if (args.error) return void this.router.navigate(['/login']);
    if (args.pendingActivation) return void this.router.navigate(['/registration'], { queryParams: { pending: 'true' } });
    if (args.requiresCompanySetup) {
      const queryParams: Record<string, string> = {};
      if (args.setupToken) queryParams['setupToken'] = args.setupToken;
      if (args.planName) queryParams['planName'] = args.planName;
      if (args.coupon) queryParams['coupon'] = args.coupon;
      return void this.router.navigate(['/auth/setup-company'], { queryParams });
    }
    if (args.checkoutUrl) {
      if (args.expiresAt) this.authService.setSession({ expires_at: args.expiresAt });
      if (this.electronService.isElectron) {
        this.electronService.openExternal(args.checkoutUrl);
        this.router.navigate(['/main'], { replaceUrl: true });
      } else {
        window.location.href = args.checkoutUrl;
      }
      return;
    }
    if (args.requires2fa && args.mfaToken) {
      this.authService.setMfaToken(args.mfaToken);
      // Sign-in is not finished yet, so hold the destination until the code is accepted.
      this.authService.rememberReturnUrl(args.returnUrl);
      return void this.router.navigate(['/login/verify-2fa']);
    }
    if (args.expiresAt) {
      this.authService.setSession({ expires_at: args.expiresAt });
      if (this.authService.redirectToReturnUrl(args.returnUrl)) return;
      return void this.router.navigate(['/main'], { replaceUrl: true });
    }
    if (this.authService.isAccessTokenValid()) {
      if (this.authService.redirectToReturnUrl(args.returnUrl)) return;
      return void this.router.navigate(['/main'], { replaceUrl: true });
    }

    this.router.navigate(['/login']);
  }
}
